import * as React from "react";
import { usePopupContext } from "src/hooks/usePopup";
import { Field } from "src/search/types";
import { GroupByIcon } from "src/components/GroupByIcon";
import { Clause } from "./Clause";
import { GroupByInputClause } from "./definitions";
import { FieldSelector } from "./FieldSelector";
import { theme, Space } from "antd";
import styles from "./question-input.module.css";
import { FieldTooltip } from "./ClauseTooltips";
import { useEntitySelectors } from "src/search/useEntitySelectors";

// Panel component that handles its own state independently
function GroupByPanel({
  initialValue,
  value,
  onApply,
  onCancel,
}: {
  initialValue?: Field;
  value: any;
  onApply: (field: Field) => void;
  onCancel: () => void;
}) {
  const { token } = theme.useToken();
  const [selectedField, setSelectedField] = React.useState<Field | undefined>(
    initialValue
  );

  const { groupBys: relevantFields } = useEntitySelectors({ state: value });

  // Reset field when panel opens with new initial value
  React.useEffect(() => {
    setSelectedField(initialValue);
  }, [initialValue]);

  // Handle field selection
  const handleFieldChange = (field: Field | undefined) => {
    setSelectedField(field);
  };

  return (
    <div className={styles.clausePanel} onClick={(e) => e.stopPropagation()}>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Field Selection */}
        <div>
          <FieldTooltip type="groupByField">
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: token.colorTextSecondary,
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Group By Property
            </div>
          </FieldTooltip>
          <FieldSelector
            value={selectedField}
            onChange={handleFieldChange}
            placeholder="Search for a field to group by..."
            defaultOpen={true}
            relevantFields={relevantFields}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 16,
            marginTop: 8,
          }}
        >
          <div
            style={{
              fontSize: "0.85em",
              color: token.colorTextDescription,
            }}
          >
            {(() => {
              if (!selectedField) return "Select a field to group by";
              return "Ready to apply grouping";
            })()}
          </div>
          <Space>
            <button
              onClick={onCancel}
              style={{
                background: "none",
                border: "none",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                color: "rgba(0, 0, 0, 0.65)",
                fontSize: 14,
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.04)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Cancel
            </button>
            <button
              style={{
                fontSize: 14,
                backgroundColor: "#1677ff",
                color: "white",
                border: "none",
                padding: "5px 16px",
                borderRadius: 6,
                cursor: selectedField ? "pointer" : "not-allowed",
                opacity: selectedField ? 1 : 0.5,
                transition: "all 0.2s",
                boxShadow: "0 2px 0 rgba(5, 145, 255, 0.1)",
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = "#4096ff";
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = "#1677ff";
                }
              }}
              disabled={!selectedField}
              onClick={() => selectedField && onApply(selectedField)}
            >
              Apply
            </button>
          </Space>
        </div>
      </Space>
    </div>
  );
}

// Pill component that displays the current value
function GroupByPill({ field }: { field?: Field }) {
  return (
    <span>
      {field && (
        <>
          <span style={{ opacity: 0.65 }}>{field.target}.</span>
          <span>{field.attribute}</span>
        </>
      )}
    </span>
  );
}

export function GroupByClause({
  clause,
  value,
  updateClause,
  removeClause,
  sameAsLast,
}: {
  clause: GroupByInputClause;
  value: any;
  updateClause: (clause: any) => void;
  removeClause: (clause: any) => void;
  sameAsLast?: boolean;
}) {
  const popup = usePopupContext();

  const handleApply = (field: Field) => {
    updateClause({
      ...clause,
      value: field,
    });
    popup.close();
  };

  return (
    <Clause
      onRemove={() => removeClause(clause)}
      onMenuClose={() => popup.close()}
      renderMenu={() => (
        <GroupByPanel
          initialValue={clause.value}
          onApply={handleApply}
          onCancel={() => popup.close()}
          value={value}
        />
      )}
      clause={clause}
      title={sameAsLast ? "" : "Groups"}
      icon={
        sameAsLast ? undefined : (
          <GroupByIcon size={10} style={{ opacity: 0.7 }} />
        )
      }
    >
      <GroupByPill field={clause.value} />
    </Clause>
  );
}
